import type { ReactElement } from 'react';
import type { GetStaticProps, GetStaticPropsContext } from 'next';
import type { CategoriesViewProps, CategoryRequestResultProps } from '../types';

import * as React from 'react';

import { View } from '../generated/collections';
// import { CollectionsA as View } from '../containers/CollectionsA';
import { BaseLayout } from '../layouts/BaseLayout';

import { NextSeo, CollectionPageJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { getAllCollections, getCollection } from '../app/api';
import { mappingCollection } from '../app/mappings';
import { useAppContext } from '../contexts/AppContext';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { cleanSlug, getLocalizedPaths, getLocalizedSlugs } from '../utils/localize-helpers';
import { CollectionTypesEnum } from '../types/enum';
import { translateUrl } from 'next-translate-routes';
import { getNonPageNamespaces } from '../utils/get-non-page-namespaces';

export default function CategoriesPage(props: CategoriesViewProps) {
  const { collections, pageContext } = props;
  const { t, i18n } = useTranslation(['common', 'collections']);
  const appContext = useAppContext();
  const { name, assetAbsoluteUrl } = appContext.useConfig();
  const router = useRouter();
  const pageContextWithPaths = {
    ...pageContext,
    paths: getLocalizedPaths(router),
    slugs: getLocalizedSlugs(router),
    slug: cleanSlug(router.pathname),
  };

  return (
    <>
      <NextSeo
        title={
          i18n.exists('collections:title', { fallbackLng: pageContextWithPaths.locale })
            ? t('collections:title')
            : t('common:title')
        }
        description={
          i18n.exists('collections:description', { fallbackLng: pageContextWithPaths.locale })
            ? t('collections:description')
            : t('common:description')
        }
      />
      <CollectionPageJsonLd
        name={t('common:title')}
        hasPart={collections.map((collection) => ({
          about: collection.description,
          author: { name },
          name: collection.name,
          audience: 'Internet',
          keywords: 'schema',
          thumbnailUrl: `${assetAbsoluteUrl}${
            collection.images.cover1x1 || collection.images.cover
          }/?ar[r]=1x1&ar[w]=512&fmt=.png`,
          image: `${assetAbsoluteUrl}${collection.images.cover1x1 || collection.images.cover}/`,
        }))}
      />
      <BreadcrumbJsonLd
        itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
          position: index + 1,
          name: crumb.name,
          item: crumb.href,
        }))}
      />
      <View
        {...props}
        title={
          i18n.exists('collections:headline', { fallbackLng: pageContextWithPaths.locale })
            ? t('collections:headline')
            : t('common:headline')
        }
        body={
          i18n.exists('collections:body', { fallbackLng: pageContextWithPaths.locale })
            ? t('collections:body')
            : t('common:body')
        }
        pageContext={pageContextWithPaths}
      />
    </>
  );
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
CategoriesPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;
  return (
    <BaseLayout pageType="collections" pageContext={pageContext}>
      {page}
    </BaseLayout>
  );
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const locale: string = (context.locale || context.defaultLocale) as string;
  const customIndexCollections = process.env.PROJECT_PAGE_INDEX_COLLECTIONS;
  let collections: CategoryRequestResultProps[];
  const isCustomCollections = Array.isArray(customIndexCollections) && customIndexCollections.length > 0;

  if (isCustomCollections) {
    const withoutSliderCategories = customIndexCollections.filter(
      (collection) => !collection?.slug?.includes('slider')
    );
    const promises: Promise<CategoryRequestResultProps | undefined>[] = withoutSliderCategories.map((collection) =>
      getCollection(
        collection.slug,
        collection?.type || CollectionTypesEnum.COLLECTION,
        {
          _fieldsGame: 'image', // Include "image" property for each game.
          _gameLimit: collection?.query?.limit || 12,
        },
        {
          ssr: true,
          locale,
        }
      )
    );
    collections = (await Promise.all(promises)) as CategoryRequestResultProps[];
  } else {
    collections = (await getAllCollections(
      CollectionTypesEnum.COLLECTION,
      {
        _fieldsGame: 'image', // Include "image" property for each game.
        _gameLimit: 12,
        _limit: 12,
      },
      {
        ssr: true,
        locale,
      }
    )) as CategoryRequestResultProps[];
  }

  if (!collections) return { notFound: true };

  const page = {
    title: null,
    body: null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    slugs: [],
    slug: 'collections',
    breadcrumbs: [
      { href: url, path: '/', name: 'Home' },
      {
        href: `${url}${translateUrl('/collections', locale)}`,
        path: `${translateUrl('/collections', locale)}`,
        name: 'Collections', // Todo: HUZ2ERA-1112 - Translate crumb name on server side?
      },
    ],
  };

  return {
    props: {
      title,
      body,
      collections: Array.isArray(collections)
        ? collections.map((collection: CategoryRequestResultProps) => ({
            ...mappingCollection(collection),
          }))
        : [],
      global: {
        popular: [],
        latest: [],
      },
      pageContext,
      // Get our translations by namespace and locale.
      // https://github.com/isaachinman/next-i18next#serversidetranslations
      ...(await serverSideTranslations(locale as string, ['common', 'collections', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
};
